import { useEffect, useMemo, useState } from "react";

import styled from "styled-components";

import { useTranslate } from "@similarweb/i18n-provider";
import { Table } from "@similarweb/pro-logic-components";
import type { SubtitleFilter } from "@similarweb/pro-logic-components";

import type { IFlattenedCategory } from "app/common/services/categoryService.types";
import type { ISwitcherConfig } from "app/components/React/WidgetContainer/types";
import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { renderIndustryDataAvailabilityNotice } from "app/pages/digital-marketing/ad-intelligence/common/data-availability/IndustryDataAvailabilityBanner";
import { useSelectedIndustry } from "app/pages/digital-marketing/ad-intelligence/shared/components/AssetPicker/useSelectedIndustry";
import { getFaviconUrl } from "app/services/sitesService/sitesUtils";

import { useDurationSubtitleFilter } from "../../../hooks/useDurationSubtitleFilter";
import { SWITCHER_OPTIONS, SwitcherOption } from "../../../shared/ShareTypeSwitcher";
import { getShareValueBySwitcherOption } from "../../utils/utils";

import { getColumns } from "./IndustryTopDomainsColumns";
import type { ITopDomainRow } from "./IndustryTopDomainsColumns";
import { useIndustryTopDomains } from "./useIndustryTopDomains";

const TOP_DOMAINS_PAGE_SIZE = 10;
const FIRST_PAGE = 1;

// Ordered to match the design (Spend first) rather than the shared switcher's
// internal enum order (Visits/Spend/Impressions).
const METRIC_TAB_ORDER: SwitcherOption[] = [SwitcherOption.Spend, SwitcherOption.Impressions, SwitcherOption.Visits];

const SHARE_COLUMN_TITLE_KEY_BY_METRIC: Record<SwitcherOption, string> = {
    [SwitcherOption.Spend]: "ad.intelligence.advertiser.publishers.spend.share.excel.column.title",
    [SwitcherOption.Impressions]: "ad.intelligence.advertiser.publishers.impressions.share.excel.column.title",
    [SwitcherOption.Visits]: "ad.intelligence.advertiser.publishers.visits.share.excel.column.title",
};

// WidgetContainer's shared BodyWrapper applies a 2px bottom padding when the
// widget has no footer, which surfaces as a stray sliver of the card beneath the
// flush table. Neutralize it here instead of altering the shared component.
const TopDomainsWidgetContainer = styled(WidgetContainer)`
    & > div > div:last-child {
        padding-bottom: 0;
    }
`;

interface IIndustryTopDomainsProps {
    category: IFlattenedCategory;
}

export const IndustryTopDomains = ({ category }: IIndustryTopDomainsProps) => {
    const { translate } = useTranslate();
    const { replaceWithDomain } = useSelectedIndustry();
    const { data, isLoading, isError, availability } = useIndustryTopDomains(category);
    const [selectedMetric, setSelectedMetric] = useState<SwitcherOption>(SwitcherOption.Spend);
    const [currentPage, setCurrentPage] = useState(FIRST_PAGE);

    const durationSubtitleFilter = useDurationSubtitleFilter();
    const subtitleFilters = useSubtitleFilters("country", "webSource");
    const combinedSubtitleFilters: SubtitleFilter[] = [durationSubtitleFilter, ...subtitleFilters];

    const rows = useMemo<ITopDomainRow[]>(
        () =>
            data
                .map((item) => ({
                    domain: item.domain,
                    favicon: getFaviconUrl(item.domain),
                    share: getShareValueBySwitcherOption(
                        selectedMetric,
                        item.spendShare,
                        item.impressionsShare,
                        item.visitsShare,
                    ),
                }))
                .sort((a, b) => b.share - a.share),
        [data, selectedMetric],
    );

    useEffect(() => {
        setCurrentPage(FIRST_PAGE);
    }, [selectedMetric, category]);

    const paginatedRows = useMemo(
        () => rows.slice((currentPage - 1) * TOP_DOMAINS_PAGE_SIZE, currentPage * TOP_DOMAINS_PAGE_SIZE),
        [rows, currentPage],
    );

    const hasData = rows.length > 0;
    const hasMultiplePages = rows.length > TOP_DOMAINS_PAGE_SIZE;
    const showEmptyState = !isLoading && (!hasData || isError);

    const switcherConfig: ISwitcherConfig = {
        items: METRIC_TAB_ORDER.map((option) => ({
            title: SWITCHER_OPTIONS[option],
            value: option,
        })),
        initialSelectedItem: METRIC_TAB_ORDER.indexOf(selectedMetric),
        onItemClick: (index: number) => setSelectedMetric(METRIC_TAB_ORDER[index]),
    };

    return (
        <TopDomainsWidgetContainer
            title={translate("ad.intelligence.advertiser.top.domains.table.title")}
            subtitleFilters={combinedSubtitleFilters}
            switcherConfig={switcherConfig}
            isLoading={isLoading}
            showSubtitleFiltersLoadingState={false}
            withBorder
            noData={showEmptyState}
            automationId="industry-top-domains-widget"
            noticeSlot={renderIndustryDataAvailabilityNotice(availability)}
        >
            <Table
                theme="white"
                bordersConfig={{ body: false, header: false }}
                columnConfig={getColumns(
                    currentPage,
                    TOP_DOMAINS_PAGE_SIZE,
                    translate("analysis.source.ads.overview.toppublishers.table.domain.column.title"),
                    translate(SHARE_COLUMN_TITLE_KEY_BY_METRIC[selectedMetric]),
                    replaceWithDomain,
                )}
                data={paginatedRows}
                isLoading={isLoading}
                loaderRowCount={TOP_DOMAINS_PAGE_SIZE}
                withArrowButtons={hasMultiplePages}
                pagination={
                    hasMultiplePages
                        ? {
                              pageSize: TOP_DOMAINS_PAGE_SIZE,
                              total: rows.length,
                              current: currentPage,
                              onChange: (_page, _pageSize, nextPage) => {
                                  setCurrentPage(nextPage);
                              },
                          }
                        : undefined
                }
            />
        </TopDomainsWidgetContainer>
    );
};
